import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Truck, Factory, Award, ChevronRight, Star, Phone, MessageCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/products/ProductCard';
import { reviews, advantages, companyInfo, standardEquipment } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Компонент для ленивой загрузки фонового изображения
const LazyBackgroundSection = ({ children, imageUrl, className = '' }) => {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '200px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className={`relative ${className}`}>
      {isInView && (
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <img 
            src={imageUrl}
            alt="Background"
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      {children}
    </div>
  );
};

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          fetch(`${API_URL}/api/products`),
          fetch(`${API_URL}/api/categories`)
        ]);
        
        if (productsRes.ok) {
          const data = await productsRes.json();
          setProducts(data);
        }
        
        if (categoriesRes.ok) {
          const data = await categoriesRes.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, []);

  const hitProducts = products.filter(p => p.isHit || p.isNew).slice(0, 4);

  return (
    <Layout>
      {/* Promotion Banner */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 text-center">
            <span className="text-lg md:text-xl font-bold">🎁 Закажите теплицу сейчас</span>
            <span className="text-sm md:text-base">— получите дополнительную форточку и крепёж в подарок!</span>
            <Link to="/catalog">
              <Button size="sm" variant="secondary" className="bg-white text-orange-600 hover:bg-gray-100 font-semibold">
                Выбрать теплицу
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-50 via-white to-green-50 overflow-hidden min-h-[600px]">
        {/* Background decorative elements */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-200 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-green-200 rounded-full blur-3xl" />
        </div>
        
        {/* Hero Image - Full width background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/36886875-1b84-4af9-be4f-b64aec9afd7a/images/9ab19100471498b12c642739927df0bd3e6bea19d41b0070f3d2b0e209def002.png')`,
              backgroundPosition: '90% center'
            }}
          />
          {/* Gradient overlay - fade from left to right */}
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-50 via-emerald-50/95 via-40% to-emerald-50/10" />
        </div>
        
        <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Теплицы из поликарбоната
              <span className="text-emerald-600"> от производителя</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed">
              Качественные теплицы для вашего участка с гарантией до 10 лет. 
              Доставка по Башкортостану и Татарстану: Туймазы, Белебей, Октябрьский, Бавлы и другие города.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/catalog">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 h-14 text-base animate-pulse hover:animate-none hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl">
                  Перейти в каталог
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href={`tel:${companyInfo.phoneClean}`}>
                <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-8 h-14 text-base bg-white/80 backdrop-blur-sm">
                  <Phone className="w-5 h-5 mr-2" />
                  Позвонить
                </Button>
              </a>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-6 mt-10 pt-8 border-t border-emerald-200/50">
              <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-3 py-2 rounded-lg">
                <ShieldCheck className="w-6 h-6 text-emerald-600" />
                <span className="text-sm text-gray-700 font-medium">Гарантия до 10 лет</span>
              </div>
              <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-3 py-2 rounded-lg">
                <Truck className="w-6 h-6 text-emerald-600" />
                <span className="text-sm text-gray-700 font-medium">Доставка по Башкортостану</span>
              </div>
              <div className="flex items-center gap-2">
                <Factory className="w-6 h-6 text-emerald-600" />
                <span className="text-sm text-gray-600">Собственное производство</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories - with lazy background */}
      <LazyBackgroundSection 
        imageUrl="https://polyhut-store.preview.emergentagent.com/api/uploads/background1.jpg"
        className="py-16 bg-white overflow-hidden"
      >
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Наши теплицы</h2>
          <p className="text-gray-600 text-center mb-10">Выберите подходящую модель</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { id: 'standart', name: 'Стандарт', price: 21500, desc: 'каркас 20х20, шаг дуг 1м', isHit: false },
              { id: 'standart-usilennaya', name: 'Стандарт Усиленная', price: 23000, desc: 'каркас 20х20, шаг дуг 65см', isHit: false },
              { id: 'bogatyr', name: 'Богатырь', price: 25000, desc: 'каркас 25х25, шаг дуг 65см', isHit: false },
              { id: 'premium', name: 'Премиум', price: 30500, desc: 'каркас 40х20, шаг дуг 65см', isHit: true },
              { id: 'domikom', name: 'Домиком - Прямостенная', price: 34500, desc: 'прямые стены, двускатная крыша', isHit: false }
            ].map((cat) => (
              <Link 
                key={cat.id}
                to={`/catalog?category=${cat.id}`}
                className="p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100 hover:shadow-lg hover:border-emerald-200 transition-all duration-300 group relative"
              >
                {cat.isHit && (
                  <Badge className="absolute top-3 right-3 bg-orange-500 text-white">
                    Хит продаж
                  </Badge>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-700">{cat.name}</h3>
                <p className="text-2xl font-bold text-emerald-600 mb-2">от {cat.price.toLocaleString('ru-RU')} ₽</p>
                <p className="text-sm text-gray-600">{cat.desc}</p>
              </Link>
            ))}
          </div>

          {/* Standard Equipment */}
          <div className="mt-12 p-6 bg-gray-50 rounded-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Стандартная комплектация всех теплиц:</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {standardEquipment.map((item, index) => (
                <span key={index} className="px-4 py-2 bg-white rounded-lg text-sm text-gray-600 border border-gray-200">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </LazyBackgroundSection>

      {/* Advantages */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((item, index) => (
              <div 
                key={item.id}
                className="p-6 rounded-2xl bg-white border border-gray-100 hover:shadow-lg hover:border-emerald-200 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center mb-4">
                  {index === 0 && <Factory className="w-6 h-6 text-white" />}
                  {index === 1 && <ShieldCheck className="w-6 h-6 text-white" />}
                  {index === 2 && <Truck className="w-6 h-6 text-white" />}
                  {index === 3 && <Award className="w-6 h-6 text-white" />}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Products */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Популярные модели</h2>
              <p className="text-gray-600">Выбор наших клиентов</p>
            </div>
            <Link to="/catalog" className="hidden md:flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium">
              Все теплицы
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {hitProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link to="/catalog">
              <Button variant="outline" className="border-emerald-600 text-emerald-700">
                Смотреть все
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews - with lazy background */}
      <LazyBackgroundSection 
        imageUrl="https://polyhut-store.preview.emergentagent.com/api/uploads/background2.jpg"
        className="py-16 bg-emerald-50 overflow-hidden"
      >
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Отзывы клиентов</h2>
            <p className="text-gray-600">Что говорят о нас покупатели из {companyInfo.regionRod}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviews.slice(0, 4).map(review => (
              <div 
                key={review.id}
                className="bg-white p-6 rounded-2xl shadow-sm border border-emerald-100"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-4 h-4 ${i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} 
                    />
                  ))}
                </div>
                <p className="text-gray-600 text-sm mb-4 line-clamp-4">{review.text}</p>
                <div className="pt-4 border-t border-gray-100">
                  <p className="font-semibold text-gray-900">{review.name}</p>
                  <p className="text-sm text-emerald-600">{review.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </LazyBackgroundSection>

      {/* CTA Section */}
      <section className="py-16 bg-emerald-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h2 className="text-3xl font-bold text-white mb-3">
                Нужна консультация?
              </h2>
              <p className="text-emerald-200 text-lg">
                Позвоните или напишите нам - поможем с выбором теплицы!
              </p>
            </div>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href={`tel:${companyInfo.phoneClean}`}>
                <Button size="lg" className="bg-white text-emerald-800 hover:bg-emerald-50 h-14 px-8">
                  <Phone className="w-5 h-5 mr-2" />
                  {companyInfo.phone}
                </Button>
              </a>
              <a href={companyInfo.social.whatsapp} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-14 px-6">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
              <a href={companyInfo.social.telegram} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-14 px-6">
                  <Send className="w-5 h-5 mr-2" />
                  Telegram
                </Button>
              </a>
              <a href={companyInfo.social.max} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-14 px-6">
                  <span className="font-bold mr-2">M</span>
                  Max
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Cities */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            Купить теплицу из поликарбоната в {companyInfo.regionPredl}
          </h3>
          <p className="text-gray-500 text-sm">
            Доставка теплиц: {companyInfo.cities.join(" • ")}
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4">
          <p className="text-xs text-gray-400 text-center leading-relaxed">
            Информация на сайте не является публичной офертой, определяемой положениями ст. 437 ГК РФ. 
            Подробности уточняйте по телефону <a href={`tel:${companyInfo.phoneClean}`} className="text-emerald-600 hover:underline">{companyInfo.phone}</a>.
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
